/**
 * forge.skin.v1 — canonical SkinPack for SkinForge + suite apps.
 * Safe to copy into any product (no Node deps).
 */

export const SKIN_SCHEMA = "forge.skin.v1" as const;
export const PREFS_SCHEMA = "forge.prefs.v1" as const;
export const CATALOG_SCHEMA = "forge.catalog.v1" as const;

export type SkinMode = "dark" | "light";
export type FontPack = "forge" | "clean" | "serif" | "mono";
export type SkinScope = "suite" | "user";

export const BUILTIN_FX_IDS = [
  "orbs",
  "mesh",
  "aurora",
  "stars",
  "sparkles",
  "grid",
  "ribbons",
  "hex",
  "rain",
  "flame",
  "scan",
  "none",
] as const;

export type BuiltinFxId = (typeof BUILTIN_FX_IDS)[number];

export interface PfTokens {
  "--pf-bg-deep": string;
  "--pf-bg-panel": string;
  "--pf-bg-elevated": string;
  "--pf-purple": string;
  "--pf-purple-glow": string;
  "--pf-cyan": string;
  "--pf-cyan-glow": string;
  "--pf-magenta": string;
  "--pf-text": string;
  "--pf-text-muted": string;
  "--pf-border": string;
  "--pf-success"?: string;
  "--pf-danger"?: string;
  "--pf-accent-soft"?: string;
  "--pf-glow"?: string;
  "--pf-glow-secondary"?: string;
  "--pf-shadow"?: string;
  "--pf-scrollbar"?: string;
  "--pf-font-display"?: string;
  "--pf-font-body"?: string;
}

export interface VlTokens {
  "--vl-bg": string;
  "--vl-bg-elevated": string;
  "--vl-bg-soft": string;
  "--vl-border": string;
  "--vl-text": string;
  "--vl-text-muted": string;
  "--vl-accent": string;
  "--vl-accent-soft": string;
  "--vl-accent-glow": string;
  "--vl-gold": string;
  "--vl-success": string;
  "--vl-danger": string;
  "--vl-font"?: string;
  "--vl-shadow"?: string;
  "--vl-brand-from": string;
  "--vl-brand-mid": string;
  "--vl-brand-to": string;
  "--vl-glow-secondary": string;
  "--vl-scrollbar"?: string;
  "--vl-spark"?: string;
  "--vl-spark-soft"?: string;
  "--vl-glow-spark"?: string;
}

export interface SkinPack {
  schema: typeof SKIN_SCHEMA;
  id: string;
  name: string;
  description: string;
  mode: SkinMode;
  preview: [string, string, string];
  fonts: FontPack;
  defaultFx: string;
  pf: PfTokens;
  vl?: VlTokens;
  scope: SkinScope;
  revision: number;
  createdAt: string;
  updatedAt: string;
  source?: "manual" | "grok" | "import" | "migrate";
  prompt?: string;
  /** Playable motion (IR + compiled CSS). Suite injects pack.motion.css. */
  motion?: import("./motion-spec").MotionSpec;
}

export interface SuitePrefs {
  schema: typeof PREFS_SCHEMA;
  activeSkinId: string | null;
  activeFxId: string | null;
  updatedAt: string;
}

export interface CatalogEntry {
  id: string;
  name: string;
  description: string;
  mode: SkinMode;
  preview: [string, string, string];
  defaultFx: string;
  scope: SkinScope;
  path: string;
  revision: number;
  updatedAt: string;
}

export interface SkinCatalog {
  schema: typeof CATALOG_SCHEMA;
  bucket: string;
  updatedAt: string;
  skins: CatalogEntry[];
}

const FONT_CSS: Record<
  FontPack,
  { display: string; body: string; vl: string }
> = {
  forge: {
    display: '"Orbitron", "Segoe UI", sans-serif',
    body: '"Rajdhani", "Segoe UI", sans-serif',
    vl: '"Rajdhani", "Segoe UI", "Inter", system-ui, sans-serif',
  },
  clean: {
    display: '"Segoe UI", "Inter", system-ui, sans-serif',
    body: '"Segoe UI", "Inter", system-ui, sans-serif',
    vl: '"Segoe UI", "Inter", system-ui, -apple-system, sans-serif',
  },
  serif: {
    display: 'Georgia, "Palatino Linotype", serif',
    body: 'Georgia, "Palatino Linotype", "Times New Roman", serif',
    vl: 'Georgia, "Palatino Linotype", "Times New Roman", serif',
  },
  mono: {
    display: 'ui-monospace, "Cascadia Code", Consolas, monospace',
    body: 'ui-monospace, "Cascadia Code", Consolas, monospace',
    vl: '"Cascadia Code", "Consolas", "SF Mono", monospace',
  },
};

const CSS_SAFE =
  /^(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|color-mix\([^)]+\)|transparent|inherit|currentColor|"[^"]*"|'[^']*'|[a-zA-Z0-9\s\-,.%"()/]+)$/;

export function isBuiltinFx(id: string): id is BuiltinFxId {
  return (BUILTIN_FX_IDS as readonly string[]).includes(id);
}

export function slugifySkinId(id: string): string {
  return id
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/** Stable user shelf key from email (not reversible display). */
export function userKeyFromEmail(email: string): string {
  const e = email.trim().toLowerCase();
  let h = 2166136261;
  for (let i = 0; i < e.length; i++) {
    h ^= e.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `u${(h >>> 0).toString(16).padStart(8, "0")}`;
}

export function deriveVlFromPf(
  pf: PfTokens,
  fonts: FontPack,
  mode: SkinMode,
): VlTokens {
  const purple = pf["--pf-purple"];
  const cyan = pf["--pf-cyan"];
  return {
    "--vl-bg": pf["--pf-bg-deep"],
    "--vl-bg-elevated": pf["--pf-bg-panel"],
    "--vl-bg-soft": pf["--pf-bg-elevated"],
    "--vl-border": pf["--pf-border"],
    "--vl-text": pf["--pf-text"],
    "--vl-text-muted": pf["--pf-text-muted"],
    "--vl-accent": purple,
    "--vl-accent-soft":
      pf["--pf-accent-soft"] ??
      `color-mix(in srgb, ${purple} 22%, transparent)`,
    "--vl-accent-glow":
      pf["--pf-glow"] ?? `color-mix(in srgb, ${purple} 45%, transparent)`,
    "--vl-gold": cyan,
    "--vl-success": pf["--pf-success"] ?? "#4ade9b",
    "--vl-danger": pf["--pf-danger"] ?? "#fb7185",
    "--vl-font": FONT_CSS[fonts].vl,
    "--vl-brand-from": pf["--pf-purple-glow"] || purple,
    "--vl-brand-mid": purple,
    "--vl-brand-to": mode === "dark" ? "#1e1b4b" : "#312e81",
    "--vl-glow-secondary":
      pf["--pf-glow-secondary"] ??
      `color-mix(in srgb, ${cyan} 28%, transparent)`,
    "--vl-scrollbar":
      pf["--pf-scrollbar"] ??
      `color-mix(in srgb, ${purple} 28%, transparent)`,
    "--vl-shadow":
      pf["--pf-shadow"] ??
      (mode === "dark"
        ? "0 18px 50px rgba(20, 8, 40, 0.55)"
        : "0 12px 36px rgba(15, 23, 42, 0.08)"),
    "--vl-spark": cyan,
    "--vl-spark-soft": `color-mix(in srgb, ${cyan} 28%, transparent)`,
    "--vl-glow-spark":
      pf["--pf-glow-secondary"] ??
      `color-mix(in srgb, ${cyan} 35%, transparent)`,
  };
}

export function resolveVl(pack: SkinPack): VlTokens {
  return pack.vl ?? deriveVlFromPf(pack.pf, pack.fonts, pack.mode);
}

export function fontCss(pack: FontPack) {
  return FONT_CSS[pack];
}

function checkTokenMap(
  tokens: Record<string, string | undefined>,
  label: string,
): string[] {
  const errors: string[] = [];
  for (const [k, v] of Object.entries(tokens)) {
    if (v == null || v === "") {
      errors.push(`${label}.${k} empty`);
      continue;
    }
    if (v.includes("url(") || v.includes("expression(") || v.includes("<")) {
      errors.push(`${label}.${k} unsafe value`);
      continue;
    }
    if (!CSS_SAFE.test(v) && v.length > 200) {
      errors.push(`${label}.${k} looks invalid`);
    }
  }
  return errors;
}

export function validateSkinPack(raw: unknown): {
  ok: boolean;
  pack?: SkinPack;
  errors: string[];
} {
  const errors: string[] = [];
  if (!raw || typeof raw !== "object") {
    return { ok: false, errors: ["not an object"] };
  }
  const o = raw as Record<string, unknown>;
  const id = slugifySkinId(String(o.id || ""));
  if (!id) errors.push("id required");
  if (!o.name) errors.push("name required");
  if (!o.pf || typeof o.pf !== "object") errors.push("pf tokens required");

  const mode = o.mode === "light" ? "light" : "dark";
  const fonts = (
    ["forge", "clean", "serif", "mono"].includes(String(o.fonts))
      ? o.fonts
      : "forge"
  ) as FontPack;
  const defaultFx = String(o.defaultFx || "orbs");
  if (!isBuiltinFx(defaultFx)) {
    // allow custom ids but warn — still ok for catalog
  }

  const pf = o.pf as PfTokens;
  const requiredPf = [
    "--pf-bg-deep",
    "--pf-bg-panel",
    "--pf-bg-elevated",
    "--pf-purple",
    "--pf-cyan",
    "--pf-text",
    "--pf-text-muted",
    "--pf-border",
  ] as const;
  for (const k of requiredPf) {
    if (!pf?.[k]) errors.push(`pf.${k} required`);
  }
  if (pf)
    errors.push(
      ...checkTokenMap(pf as unknown as Record<string, string | undefined>, "pf"),
    );

  if (errors.length) return { ok: false, errors };

  const now = new Date().toISOString();
  const preview = Array.isArray(o.preview)
    ? (o.preview as string[])
    : [pf["--pf-bg-deep"], pf["--pf-purple"], pf["--pf-cyan"]];

  const pack: SkinPack = {
    schema: SKIN_SCHEMA,
    id,
    name: String(o.name),
    description: String(o.description || ""),
    mode,
    preview: [preview[0] || "#000", preview[1] || "#888", preview[2] || "#ccc"],
    fonts,
    defaultFx,
    pf: {
      ...pf,
      "--pf-purple-glow": pf["--pf-purple-glow"] || pf["--pf-purple"],
      "--pf-cyan-glow": pf["--pf-cyan-glow"] || pf["--pf-cyan"],
      "--pf-magenta": pf["--pf-magenta"] || pf["--pf-purple"],
    },
    vl: o.vl as VlTokens | undefined,
    scope: o.scope === "user" ? "user" : "suite",
    revision: Number(o.revision) || 1,
    createdAt: String(o.createdAt || now),
    updatedAt: String(o.updatedAt || now),
    source: o.source as SkinPack["source"],
    prompt: o.prompt ? String(o.prompt) : undefined,
    motion:
      o.motion && typeof o.motion === "object"
        ? (o.motion as SkinPack["motion"])
        : undefined,
  };

  if (!pack.vl) pack.vl = deriveVlFromPf(pack.pf, pack.fonts, pack.mode);

  return { ok: true, pack, errors: [] };
}

export function toCatalogEntry(pack: SkinPack, relPath: string): CatalogEntry {
  return {
    id: pack.id,
    name: pack.name,
    description: pack.description,
    mode: pack.mode,
    preview: pack.preview,
    defaultFx: pack.defaultFx,
    scope: pack.scope,
    path: relPath,
    revision: pack.revision,
    updatedAt: pack.updatedAt,
  };
}

/** Normalize legacy SkinForge SkinDefinition → SkinPack */
export function fromLegacySkinDefinition(
  legacy: Record<string, unknown>,
  scope: SkinScope = "suite",
): SkinPack {
  const result = validateSkinPack({
    ...legacy,
    schema: SKIN_SCHEMA,
    scope,
    revision: legacy.revision ?? 1,
  });
  if (result.pack) return result.pack;
  // force minimal pack
  const now = new Date().toISOString();
  const pf = (legacy.pf || {}) as PfTokens;
  return {
    schema: SKIN_SCHEMA,
    id: slugifySkinId(String(legacy.id || "skin")),
    name: String(legacy.name || "Skin"),
    description: String(legacy.description || ""),
    mode: legacy.mode === "light" ? "light" : "dark",
    preview: (legacy.preview as [string, string, string]) || [
      "#0a0612",
      "#a78bfa",
      "#22d3ee",
    ],
    fonts: "forge",
    defaultFx: String(legacy.defaultFx || "orbs"),
    pf: {
      "--pf-bg-deep": pf["--pf-bg-deep"] || "#0a0612",
      "--pf-bg-panel": pf["--pf-bg-panel"] || "#120a1f",
      "--pf-bg-elevated": pf["--pf-bg-elevated"] || "#1a1030",
      "--pf-purple": pf["--pf-purple"] || "#a78bfa",
      "--pf-purple-glow": pf["--pf-purple-glow"] || "#c084fc",
      "--pf-cyan": pf["--pf-cyan"] || "#22d3ee",
      "--pf-cyan-glow": pf["--pf-cyan-glow"] || "#67e8f9",
      "--pf-magenta": pf["--pf-magenta"] || "#e879f9",
      "--pf-text": pf["--pf-text"] || "#f3e8ff",
      "--pf-text-muted": pf["--pf-text-muted"] || "#a78bca",
      "--pf-border": pf["--pf-border"] || "rgba(168, 85, 247, 0.25)",
    },
    scope,
    revision: 1,
    createdAt: String(legacy.createdAt || now),
    updatedAt: now,
    source: "migrate",
  };
}
